import { useQuery, useMutation, useQueryClient, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import apis from "#store/api";
import { DataApiResponse, DataCollectionApiResponse } from "./types";

const MEAL_TRACKING_KEY = 'meal-tracking';

export interface MealTracking {
  id: string;
  fullName: string;
  role: number;
  departmentOrAgency: string;
  date: Date;
  breakfast: boolean;
  isBreakfastEdited: boolean;
  breakfastQuantity?: number;
  lunch: boolean;
  isLunchEdited: boolean;
  lunchQuantity?: number;  
  dinner: boolean;
  isDinnerEdited: boolean;
  dinnerQuantity?: number;
  snacks: number;
  snacksQuantity?: number;
  personalImageUrl?: string;
}

export interface MealTrackingStatistics {
  fullAccess?: number;
  breakfast?: number;
  lunch?: number;
  dinner?: number;
  snacks?: number;
}

interface GetMealTrackingListParams {
  skip: number;
  take: number;
  date: Date;
  incidentId?: string;
  name?: string;
  departmentOrAgency?: string;
  usarTeamNumber?: number;
  role?: number;
}

interface GetMealTrackingStatisticsParams {
  date: Date;
  incidentId?: string;
  name?: string;
  departmentOrAgency?: string;
  usarTeamNumber?: number;
  role?: number;
}

export interface UpdateMealTrackingParams {
  mealTracking:{
    responderId: string;
    breakfast: boolean;
    isBreakfastEdited?: boolean;
    breakfastQuantity?: number;
    lunch: boolean;
    isLunchEdited?: boolean;
    lunchQuantity?: number;  
    dinner: boolean;
    isDinnerEdited?: boolean;
    dinnerQuantity?: number;
    snacks: number;
    snacksQuantity?: number;
    date: Date;
  }
}

export interface GetMealTrackingByIdParams {  
  id: string;  
  date: Date;  
}  

// Get meal tracking list
export const useGetMealTrackingList = (params: GetMealTrackingListParams) => {
  return useQuery({
    queryKey: [MEAL_TRACKING_KEY, params],
    queryFn: async () => {
      const response = await apis.mealTracking.getMealTrackingList({ params });
      return response.data as DataCollectionApiResponse<MealTracking>;
    },
  });
};

// Get meal tracking statistics
export const useGetMealTrackingStatistics = (params: GetMealTrackingStatisticsParams) => {
  return useQuery({
    queryKey: [MEAL_TRACKING_KEY, 'statistics', params],
    queryFn: async () => {
      const response = await apis.mealTracking.getMealTrackingStatistics({ params });
      return response.data as DataApiResponse<MealTrackingStatistics>;
    },
  });
};

// Update meal tracking
export const useUpdateMealTracking = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (params: UpdateMealTrackingParams) => {
      const response = await apis.mealTracking.updateMealTracking({ data: params });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [MEAL_TRACKING_KEY] });
      queryClient.invalidateQueries({ queryKey: [MEAL_TRACKING_KEY, 'statistics'] });
    }
  });
};

// Get by id meal tracking
export const useGetMealTrackingById = (
  params: GetMealTrackingByIdParams,
  options?: Omit<
    UseQueryOptions<DataApiResponse<MealTracking>, any, DataApiResponse<MealTracking>, any>,
    "queryKey" | "queryFn"
  >
): UseQueryResult<DataApiResponse<MealTracking>> => {
  return useQuery<DataApiResponse<MealTracking>>({
    queryKey: [MEAL_TRACKING_KEY, 'get-by-id', params],
    queryFn: async () => {
      const response = await apis.mealTracking.getMealTrackingById({ data: params });
      return { item: response.data };
    },
    enabled: !!params.id && !!params.date,
    ...options
  });
};